/**
 * TimelineFirestoreService - Handles all Firestore CRUD and query operations
 * for the timeline_events collection.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy
} from '../../firebase-config';
import {
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { firestore } from '../../firebase-config';
import { validateEvent } from '../utils/validation.js';

class TimelineFirestoreService {
  constructor(collectionName = 'timeline_events') {
    this.collectionName = collectionName;
  }

  /**
   * Switch the active collection (e.g., between live and test data).
   * @param {string} collectionName
   */
  setCollection(collectionName) {
    this.collectionName = collectionName;
  }

  /**
   * Get a reference to the timeline_events collection.
   */
  getCollectionRef() {
    return collection(firestore, this.collectionName);
  }

  /**
   * Create a new timeline event.
   * Validates the event data, adds server timestamps, and returns the generated ID.
   * @param {object} eventData - The event data to create
   * @returns {Promise<string>} The generated document ID
   */
  async createEvent(eventData) {
    const validation = validateEvent(eventData);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const docData = {
      ...eventData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(this.getCollectionRef(), docData);
    return docRef.id;
  }

  /**
   * Update an existing timeline event by ID.
   * Validates the event data and sets updatedAt to server timestamp.
   * @param {string} eventId - The document ID to update
   * @param {object} eventData - The updated event data
   * @returns {Promise<void>}
   */
  async updateEvent(eventId, eventData) {
    const validation = validateEvent(eventData);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const docRef = doc(firestore, this.collectionName, eventId);
    await updateDoc(docRef, {
      ...eventData,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Delete a timeline event by ID.
   * First queries children by parentId, batch-updates their parentId to null,
   * then deletes the parent document.
   * @param {string} eventId - The document ID to delete
   * @returns {Promise<void>}
   */
  async deleteEvent(eventId) {
    // Query children that reference this event as parent
    const childrenQuery = query(
      this.getCollectionRef(),
      where('parentId', '==', eventId)
    );
    const childrenSnapshot = await getDocs(childrenQuery);

    // Batch update children's parentId to null, then delete the parent
    const batch = writeBatch(firestore);

    childrenSnapshot.docs.forEach((childDoc) => {
      batch.update(childDoc.ref, { parentId: null });
    });

    const parentRef = doc(firestore, this.collectionName, eventId);
    batch.delete(parentRef);

    await batch.commit();
  }

  /**
   * Fetch a single event by ID.
   * @param {string} eventId - The document ID to fetch
   * @returns {Promise<object|null>} The event data with id, or null if not found
   */
  async getEvent(eventId) {
    const docRef = doc(firestore, this.collectionName, eventId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return null;
    }

    return { id: snapshot.id, ...snapshot.data() };
  }

  /**
   * Query events by year range and optional maximum depth.
   * @param {number} startYear - The start of the year range (inclusive)
   * @param {number} endYear - The end of the year range (inclusive)
   * @param {number} [maxDepth] - Optional maximum depth filter
   * @returns {Promise<object[]>} Array of matching events
   */
  async queryByRange(startYear, endYear, maxDepth) {
    let q = query(
      this.getCollectionRef(),
      where('startYear', '>=', startYear),
      where('startYear', '<=', endYear),
      orderBy('startYear', 'asc')
    );

    const snapshot = await getDocs(q);
    let results = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Filter by maxDepth client-side if specified
    // (Firestore compound queries on inequality + another field require specific indexes)
    if (maxDepth !== undefined && maxDepth !== null) {
      results = results.filter((event) => event.depth <= maxDepth);
    }

    return results;
  }

  /**
   * Query events by parentId (children of a specific event).
   * @param {string} parentId - The parent event ID
   * @returns {Promise<object[]>} Array of child events
   */
  async queryChildren(parentId) {
    const q = query(
      this.getCollectionRef(),
      where('parentId', '==', parentId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Query events that have a link targeting the given event ID.
   * Since Firestore cannot query into nested array object fields,
   * we fetch all events and filter client-side.
   * @param {string} eventId - The target event ID to find incoming links for
   * @returns {Promise<object[]>} Array of events that link to the given event
   */
  async queryIncomingLinks(eventId) {
    const snapshot = await getDocs(this.getCollectionRef());
    const allEvents = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    return allEvents.filter((event) => {
      if (!event.links || !Array.isArray(event.links)) return false;
      return event.links.some((link) => link.targetId === eventId);
    });
  }

  /**
   * Query events by category.
   * @param {string} category - The category to filter by
   * @returns {Promise<object[]>} Array of matching events
   */
  async queryByCategory(category) {
    const q = query(
      this.getCollectionRef(),
      where('category', '==', category)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Query events by tag (array-contains).
   * @param {string} tag - The tag to search for
   * @returns {Promise<object[]>} Array of events containing the tag
   */
  async queryByTag(tag) {
    const q = query(
      this.getCollectionRef(),
      where('tags', 'array-contains', tag)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
}

export default TimelineFirestoreService;
